import React, { lazy, Suspense } from "react";
import ExecutionEnvironment from "exenv";

// Components
import CustomMeta from "../components/CustomMeta/CustomMeta";
const ClaimReferral = lazy(() =>
  import(
    /* webpackChunkName: "ClaimReferral" */ "../components/Referrals/ClaimReferral"
  )
);

const LoginOrSignup = lazy(() =>
  import(
    /* webpackChunkName: "LoginOrSignup" */ "../components/Login/LoginOrSignup"
  )
);

// Mui stuff
import { Typography, Box, Grid, Paper } from "@mui/material";

import { makeStyles } from "tss-react/mui";

// redux stuff
import { connect } from "react-redux";

const useStyles = makeStyles()((theme) => ({
  pricingDiv: {
    padding: "0px 15px 15px 15px",
    textAlign: "center",
  },
  headingDiv: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  fullPageHeight: {
    minHeight: "100vh",
    width: "100%",
  },
  claimSection: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  claimButtonContainer: {
    maxWidth: 500,
    margin: "0 auto",
    marginTop: theme.spacing(1),
  },
  contentSection: {
    padding: theme.spacing(2),
    maxWidth: 1200,
    margin: "0 auto",
  },
  featureCard: {
    padding: theme.spacing(2),
    height: "100%",
    textAlign: "center",
  },
  featureIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  benefitSection: {
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(5),
  },
  statsSection: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  statItem: {
    textAlign: "center",
    padding: theme.spacing(1),
  },
  statNumber: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  ctaText: {
    marginBottom: theme.spacing(2),
  },
}));

function referral(props) {
  const { classes } = useStyles();

  return (
    <div className={classes.fullPageHeight}>
      <CustomMeta
        title={`Referral Program - Content Generator`}
        description={`Claim your free month of Content Generator premium features through our referral program. Enjoy unlimited social media marketing tools, post creation, and automation features at no cost.`}
        url={`https://contentgenerator.io/referral`}
      />

      {/* Claim Section - Top of page */}
      <div className={classes.claimSection}>
        <div className={classes.pricingDiv}>
          <div className={classes.headingDiv}>
            <Typography variant="h1" style={{ color: "white" }}>
              Get one month free on Content Generator with this referral
            </Typography>
            <Typography variant="h5" style={{ color: "white" }}>
              Unlock the full power of social media marketing automation and
              marketing
            </Typography>
          </div>
          {ExecutionEnvironment.canUseDOM && (
            <Suspense fallback={<div />}>
              {props.user.authenticated ? (
                <div className={classes.claimButtonContainer}>
                  <ClaimReferral color={"default"} />
                </div>
              ) : (
                <div>
                  <Typography style={{ color: "white", marginBottom: 16 }}>
                    Login or sign up to claim this referral
                  </Typography>
                  <div className={classes.claimButtonContainer}>
                    <LoginOrSignup />
                  </div>
                </div>
              )}
            </Suspense>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className={classes.contentSection}>
        {/* What is Content Generator */}
        <Box mb={5}>
          <Typography variant="h2" align="center" gutterBottom>
            What is Content Generator?
          </Typography>
          <Typography variant="h5" align="center" paragraph>
            Content Generator is the the ultimate social media marketing
            automation platform that helps businesses, creators, and marketers
            grow their social media presence with powerful automation tools and
            AI-driven content creation.
          </Typography>
        </Box>

        {/* Key Features */}
        <Box>
          <Typography variant="h2" align="center" gutterBottom>
            Powerful Features for Social Media Success
          </Typography>
          <Grid container spacing={2} style={{ paddingTop: 30 }}>
            <Grid item xs={12} md={4}>
              <Paper className={classes.featureCard} elevation={2}>
                <div className={classes.featureIcon}>🎨</div>
                <Typography variant="h3" gutterBottom>
                  Generate Posts Fast
                </Typography>
                <Typography color="textSecondary">
                  Create stunning, high-converting posts in seconds with our
                  AI-powered design tools. Scan your website for images and
                  generate multiple variations and optimize for maximum
                  engagement.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.featureCard} elevation={2}>
                <div className={classes.featureIcon}>📈</div>
                <Typography variant="h3" gutterBottom>
                  Smart Automation
                </Typography>
                <Typography color="textSecondary">
                  Automate your social media strategy with intelligent
                  scheduling, reposting, and content management. Save hours
                  while growing your reach.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className={classes.featureCard} elevation={2}>
                <div className={classes.featureIcon}>🤖</div>
                <Typography variant="h3" gutterBottom>
                  AI-Powered Content Creation
                </Typography>
                <Typography color="textSecondary">
                  Use AI to generate your titles, descriptions, images and much
                  more for you social media posts.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Benefits Section */}
        <div className={classes.benefitSection}>
          <Typography variant="h2" align="center" gutterBottom>
            Why Choose Content Generator?
          </Typography>
          <Grid container spacing={4} style={{ paddingTop: 30 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom>
                🚀 Grow Your Business
              </Typography>
              <Typography paragraph>
                Drive more traffic to your website, increase sales, and build a
                loyal following with strategic social media marketing
                automation.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom>
                ⏰ Save Time & Effort
              </Typography>
              <Typography paragraph>
                Automate repetitive tasks and focus on what matters most -
                creating great content and growing your business.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom>
                🎯 Increase Engagement
              </Typography>
              <Typography paragraph>
                Optimize your posts for maximum visibility and engagement with
                AI-powered insights and best practices.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom>
                💰 Boost Revenue
              </Typography>
              <Typography paragraph>
                Convert social media traffic into customers with optimized
                content and strategic automation that drives results.
              </Typography>
            </Grid>
          </Grid>
        </div>

        {/* Stats Section */}
        <div className={classes.statsSection}>
          <Typography variant="h2" align="center" gutterBottom>
            Trusted by Thousands of Marketers
          </Typography>
          <Grid container spacing={4} style={{ marginTop: 16 }}>
            <Grid item xs={12} md={3}>
              <div className={classes.statItem}>
                <div className={classes.statNumber}>30K+</div>
                <Typography variant="h6">Users</Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={3}>
              <div className={classes.statItem}>
                <div className={classes.statNumber}>5M+</div>
                <Typography variant="h6">Posts Created</Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={3}>
              <div className={classes.statItem}>
                <div className={classes.statNumber}>230%</div>
                <Typography variant="h6">Avg. Traffic Increase</Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={3}>
              <div className={classes.statItem}>
                <div className={classes.statNumber}>24/7</div>
                <Typography variant="h6">Automation</Typography>
              </div>
            </Grid>
          </Grid>
        </div>

        {/* Final CTA */}
        <Box mt={5} mb={5} textAlign="center">
          <Typography variant="h2" gutterBottom>
            Ready to Transform Your Social Media Marketing?
          </Typography>
          <Typography variant="h5" paragraph className={classes.ctaText}>
            Claim your free month and experience the power of social media
            marketing automation today.
          </Typography>
          {ExecutionEnvironment.canUseDOM && (
            <Suspense fallback={<div />}>
              {props.user.authenticated ? (
                <div className={classes.claimButtonContainer}>
                  <ClaimReferral />
                </div>
              ) : (
                <div>
                  <Typography style={{ marginBottom: 16 }}>
                    Login or sign up to claim this referral
                  </Typography>
                  <div className={classes.claimButtonContainer}>
                    <LoginOrSignup />
                  </div>
                </div>
              )}
            </Suspense>
          )}
        </Box>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  data: state.data,
  user: state.user,
});

export default connect(mapStateToProps)(referral);
